import React, {CSSProperties, ReactNode, useContext } from 'react';
import { FlexTemplateDefinition, TemplatePanel, TemplateArea } from '../../layout/templatepanel';
import { Tooltip } from '../tooltip';
import { Text } from '../text';
import { FormContext, FormContextType, generateFormFieldId } from '../../data-entry/form';
import { Icons } from '@unifocus/react-icons';
import classnames from 'classnames';

/**
 * {@docCategory Label}
 */
export interface LabelProps {

   /**
    * Whether or not the label is inline or vertical with the child element.
    */
   inline?: boolean;

   /**
    * The text of the label
    */
   text?: string;

   /**
    * The help text of the label.  Setting this property will cause an info icon to be shown
    * that will have a tooltip containing this text.
    */
   helpText?: string;

   /**
    * Optional hint text that will be visible under the child element.
    */
   hint?: string;

   /**
    * Optional extra element that will be visible under the child element and hint text.  For
    * form items, this is the error text.
    */
   extra?: ReactNode;

   /**
    * Whether or not to show the required mark (asterisk).
    */
   showRequiredMark?: boolean;

   /**
    * The maximum width that the label can occupy.  For inline labels, this is the total width
    * allocated to the label.  This defaults to the width of the label text when not in a
    * FormPanel.
    */
   maxLabelWidth?: string | number;

   /**
    * The maximum width that the hint and extra content can occupy.  This should generally match
    * the child element width.
    */
   maxHintWidth?: string | number;

   /**
    * Set this to disable word wrap on the label text
    */
   disableLabelWordWrap?: boolean;

   /**
    * Allow the label contents to stretch to its parent width
    */
   horizontalFill?: boolean;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * The theme
    */
   theme?: 'light' | 'dark';
}

export const Label = (props: React.PropsWithChildren<LabelProps>) => {

   const maxLabelWidth = props.maxLabelWidth || 'max-content';
   const maxHintWidth = props.maxHintWidth || '250px';

   const formContext = useContext(FormContext);

   const childrenIdNameResult = getChildrenIdNameResult(formContext, props.children);
   const children = childrenIdNameResult.children;
   const childId = childrenIdNameResult.id;

   let text = props.text;
   if (text && text.trimEnd().endsWith(':')) {
      text = text.trimEnd();
      text = text.substring(0, text.length - 1);
   }

   const maxWidth = props.horizontalFill ? '100%' : 'max-content';
   const width = props.horizontalFill ? '100%' : 'auto';

   return (
      <TemplatePanel width={width} maxWidth={maxWidth} style={props.style} template={props.inline ? getHorizontalTemplate(maxLabelWidth, maxHintWidth, props.horizontalFill === true) : getVerticalTemplate(maxLabelWidth, maxHintWidth, props.horizontalFill === true)} className={`uf-label ${props.inline ? 'uf-label-inline' : ''} ${props.className ? props.className : ''}`}>
         <TemplateArea areaName={'label'} className={'uf-label-label-area'}>
            <InnerLabel inline={props.inline}
                        disableLabelWordWrap={props.disableLabelWordWrap}
                        showRequiredMark={props.showRequiredMark}
                        text={text}
                        helpText={props.helpText}
                        childId={childId}
                        theme={props.theme}
            />
         </TemplateArea>
         <TemplateArea
            areaName={'content'}
            className={classnames('uf-label-content',{ 'uf-label-content-horizontal-fill': props.horizontalFill })}
         >
            {children}
         </TemplateArea>
         <TemplateArea areaName={'label-space'}/>
         <TemplateArea areaName={'hint'}>
            {
               props.hint && <Text type={'secondary'} className={'uf-label-hint'}>{props.hint}</Text>
            }
         </TemplateArea>
         <TemplateArea areaName={'extra'}>
            {props.extra ? props.extra : null}
         </TemplateArea>
      </TemplatePanel>
   );
}

interface InnerLabelProps {
   inline?: boolean;
   disableLabelWordWrap?: boolean;
   showRequiredMark?: boolean;
   text?: string;
   helpText?: string;
   childId?: string;
   theme?: string;
}

const InnerLabel = (props: InnerLabelProps) => {

   if (props.text === undefined) {
      return null;
   }

   const lastWordIndex = props.text.search(/\b(\w+)\W*$/);

   let lastWord;
   let firstWords;
   if (props.disableLabelWordWrap) {
      lastWord = props.text;
   }
   else {
      lastWord = props.text.substr(lastWordIndex);
      firstWords = props.text.substr(0, lastWordIndex);
   }
   const classNames = classnames('uf-label-label', {'uf-label-label-dark': props.theme === 'dark'});
   return (<label className={classNames} htmlFor={props.childId} title={props.text}>
      {props.showRequiredMark ? <span className={'uf-label-required-mark'}>*</span> : null}
      <span className={'uf-label-first-words'}>{firstWords}</span>
      <span className={'uf-label-last-word'}>
                  {lastWord}
         {props.helpText ? <InfoIcon infoIconTooltip={props.helpText} /> : null}
         {props.inline && props.text ? ':' : ''}
      </span>
   </label>);
}

interface ChildrenIdNameResult {
   children?: ReactNode;
   id?: string;
}

const getChildrenIdNameResult = (formContext: FormContextType, children: ReactNode | undefined): ChildrenIdNameResult => {

   if (children === undefined || typeof children === 'string' || React.Children.count(children) !== 1 || Array.isArray(children)) {
      return {
         children: <span>{children}</span>
      };
   }

   const child = React.Children.only(children);
   if (!React.isValidElement(child) || child.props === undefined || child.props.name === undefined) {
      return {
         children: children
      };
   }

   const inputId = generateFormFieldId(formContext.name, child.props.name);
   return {
      children: child,
      id: inputId
   }
}

interface InfoIconProps {
   infoIconTooltip?: string;
}

const InfoIcon = (props: InfoIconProps) => {

   if (props.infoIconTooltip !== undefined) {
      return (
         <Tooltip title={props.infoIconTooltip}>
            <Icons.Information size={'sm'} className={'uf-label-info-icon'}/>
         </Tooltip>
      );
   }
   return null;
}

const getHorizontalTemplate = (maxLabelWidth: string | number, maxHintWidth: string | number, horizontalFill: boolean): FlexTemplateDefinition => {
   return {
      rows: [{
         flex: 1,
         flexDirection: 'row',
         width: horizontalFill ? '100%' : 'max-content',
         maxWidth: horizontalFill ? '100%' : 'max-content',
         flexItems: {
            'label-column': {
               flex: 1,
               minWidth: maxLabelWidth,
               maxWidth: maxLabelWidth,
               flexDirection: 'column',
               flexItems: {
                  'label': {
                     flex: 1,
                     minHeight: '30px',
                     alignSelf: 'flex-end',
                     alignItems: 'flex-end'
                  },
                  'label-space': {
                     flex: 1,
                  },
                  'more-label-space': {
                     flex: 1,
                  },
               }
            },
            'field-column': {
               flex: 1,
               flexDirection: 'column',
               alignSelf: 'flex-end',
               width: 'auto',
               minWidth: '0px',
               flexItems: {
                  'content': {
                     flex: 1,
                     minHeight: '30px',
                     alignItems: 'flex-end',
                     maxWidth: horizontalFill ? '100%' : maxHintWidth,
                     width: 'auto'
                  },
                  'hint': {
                     flex: 1,
                     maxWidth: maxHintWidth,
                     width: 'auto'
                  },
                  'extra': {
                     flex: 1,
                     maxWidth: maxHintWidth,
                     width: 'auto'
                  }
               }
            }
         }
      }]
   }
}

const getVerticalTemplate = (maxLabelWidth: string | number, maxHintWidth: string | number, horizontalFill: boolean): FlexTemplateDefinition => {
   return {
      rows: [{
         flex: 1,
         flexDirection: 'column',
         width: horizontalFill ? '100%' : 'auto',
         maxWidth: horizontalFill ? '100%' : 'max-content',
         flexItems: {
            'label': {
               flex: 1,
               maxWidth: maxLabelWidth
            },
            'content': {
               flex: 1,
               width: horizontalFill ? '100%' : 'auto',
               maxWidth: horizontalFill ? '100%' : 'max-content',
            },
            'hint': {
               flex: 1,
               width: 'auto',
               maxWidth: maxHintWidth,
            },
            'extra': {
               flex: 1,
               width: 'auto',
               maxWidth: maxHintWidth
            }
         }
      }]
   }
};
